import type { Booth, BoothType } from '#admin/features/exhibitor/api.ts'
import type { CatalogItem } from '#admin/features/exhibitor/service-items-api.ts'
import { basketOf, forSaleIn } from '#admin/features/exhibitor/service-orders-api.ts'

/**
 * What the operator types at the onboarding desk, before anything is sent. The checks here are
 * for the form. The server checks everything again.
 */

export type Settlement = 'paid' | 'invoice'

export type StaffRow = { key: string; name: string; email: string; title: string }

export type DeskDraft = {
  companyMode: 'existing' | 'new'
  companyId: string
  companyName: string
  industrySector: string
  contactName: string
  contactEmail: string
  contactPhone: string
  currency: string
  boothIds: readonly string[]
  /** Service item id → the quantity as typed. */
  quantities: Readonly<Record<string, string>>
  staff: readonly StaffRow[]
  passTypeId: string
  settlement: Settlement
  paymentNote: string
}

export function emptyDraft(currency: string): DeskDraft {
  return {
    companyMode: 'existing',
    companyId: '',
    companyName: '',
    industrySector: '',
    contactName: '',
    contactEmail: '',
    contactPhone: '',
    currency,
    boothIds: [],
    quantities: {},
    staff: [],
    passTypeId: '',
    settlement: 'paid',
    paymentNote: '',
  }
}

/** A stand the desk can sell, with its type. */
export type Stand = Booth & { type: BoothType }

/**
 * The free stands sold in `currency`, by code. A stand is priced in its type's currency, and the
 * server invoices it in that currency, so the desk shows one currency at a time.
 */
export function standsIn(
  booths: readonly Booth[],
  types: readonly BoothType[],
  currency: string,
): Stand[] {
  const byId = new Map(types.map((type) => [type.id, type]))
  return booths
    .flatMap((booth) => {
      const type = byId.get(booth.boothTypeId)
      return booth.status === 'available' && type !== undefined && type.currency === currency
        ? [{ ...booth, type }]
        : []
    })
    .toSorted((a, b) => a.code.localeCompare(b.code, undefined, { numeric: true }))
}

/** The currencies the floor is sold in. The edition's own currency comes first. */
export function deskCurrencies(types: readonly BoothType[], edition: string): string[] {
  const sold = [...new Set(types.filter((type) => type.isActive).map((type) => type.currency))]
  if (sold.length === 0) return [edition]
  return sold.toSorted((a, b) => (a === edition ? -1 : b === edition ? 1 : a.localeCompare(b)))
}

/** How many convention passes the chosen stands give. The server counts the same way. */
export function passQuota(stands: readonly Stand[]): number {
  return stands.reduce((sum, stand) => sum + stand.type.conventionPasses, 0)
}

export const isEmail = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())

/** The staff rows that name somebody. An empty row is left out, not refused. */
export function namedStaff(staff: readonly StaffRow[]): StaffRow[] {
  return staff.filter((row) => row.name.trim() !== '' || row.email.trim() !== '')
}

/**
 * What stops the desk from sending, by field. Empty when it can send. A staff row's problems are
 * keyed `staff.<key>.name` and `staff.<key>.email`.
 */
export function deskIssues(
  draft: DeskDraft,
  picked: readonly Stand[],
  badgesAvailable: boolean,
): Record<string, string> {
  const issues: Record<string, string> = {}
  if (draft.companyMode === 'existing' && draft.companyId === '') {
    issues['company'] = 'Select a company, or add a new one.'
  }
  if (draft.companyMode === 'new' && draft.companyName.trim() === '') {
    issues['companyName'] = 'Enter the company name.'
  }
  if (draft.contactName.trim() === '') issues['contactName'] = 'Enter the name of the contact.'
  if (!isEmail(draft.contactEmail)) issues['contactEmail'] = 'Enter an email address.'
  if (picked.length === 0) issues['stands'] = 'Select at least one stand.'
  if (basketOf(draft.quantities).invalid.length > 0) {
    issues['services'] = 'Enter each quantity as a number, such as 2 or 1.5.'
  }

  if (draft.settlement === 'paid' && badgesAvailable) {
    const staff = namedStaff(draft.staff)
    for (const row of staff) {
      if (row.name.trim() === '') issues[`staff.${row.key}.name`] = 'Enter the name.'
      if (!isEmail(row.email)) issues[`staff.${row.key}.email`] = 'Enter an email address.'
    }
    const emails = staff.map((row) => row.email.trim().toLowerCase())
    if (new Set(emails).size < emails.length) {
      issues['staff'] = 'Each person needs a different email address.'
    }
    if (staff.length > passQuota(picked)) {
      issues['staff'] = `The stands give ${passQuota(picked)} passes. Remove a person.`
    }
    if (staff.length > 0 && draft.passTypeId === '') issues['passType'] = 'Select a badge type.'
  }
  return issues
}

/** One line of the estimate. `tax` is the line's own tax, at its own rate. */
export type EstimateLine = {
  key: string
  label: string
  detail: string
  amount: number
  tax: number
}

export type Estimate = {
  stands: EstimateLine[]
  services: EstimateLine[]
  subtotal: number
  tax: number
  total: number
}

/**
 * An estimate for the summary, before the application exists. The server sets the figures when it
 * issues the invoices: it rounds each line, and adds the installation fees and the on-site
 * surcharge of the services. So the summary calls this an estimate.
 */
export function deskEstimate(
  picked: readonly Stand[],
  catalog: readonly CatalogItem[],
  draft: Pick<DeskDraft, 'currency' | 'quantities'>,
): Estimate {
  const stands = picked.map((stand): EstimateLine => {
    const amount = Number(stand.areaSqm) * stand.ratePerSqm
    return {
      key: stand.id,
      label: `Stand ${stand.code}`,
      detail: `${stand.areaSqm} m²`,
      amount,
      tax: (amount * Number(stand.type.taxRate)) / 100,
    }
  })
  const forSale = forSaleIn(catalog, draft.currency)
  const services = basketOf(draft.quantities).items.flatMap(
    ({ serviceItemId, quantity }): EstimateLine[] => {
      const item = forSale.find((candidate) => candidate.id === serviceItemId)
      if (item === undefined) return []
      const amount = Number(quantity) * item.unitPrice
      return [
        {
          key: item.id,
          label: item.name,
          detail: `${quantity} × ${item.unit}`,
          amount,
          tax: (amount * Number(item.taxRate)) / 100,
        },
      ]
    },
  )
  const all = [...stands, ...services]
  const subtotal = all.reduce((sum, line) => sum + line.amount, 0)
  const tax = all.reduce((sum, line) => sum + line.tax, 0)
  return {
    stands,
    services,
    subtotal,
    tax,
    total: subtotal + tax,
  }
}
